import { SearchSelect } from "@result/rui/components";
import { ApiFilter } from "@result/rui/utils/api";
import PropTypes from "prop-types";

import { useDataLayerQuery } from "#/hooks/useDataLayer";

/**
 * A reusable SearchSelect component for searching data from the data layer.
 *
 * This is a functional component that connects the SearchSelect component
 * from RUI to the data layer using the useDataLayerQuery hook.
 *
 * This component is a wrapper around the SearchSelect component from RUI,
 * which provides the UI for searching and selecting items.
 * The DataLayerSearchSelect component connects it to the data layer
 * using the useDataLayerQuery hook.
 *
 * The component takes an ident prop which is used to identify the data
 * layer resource to query. It also takes queryFields which are used to
 * build the search filter when the user types in the search input. The filter
 * prop can be used to provide additional filtering logic.
 *
 * Example usage:
 *   <DataLayerSearchSelect
 *    ident="my-data-layer-endpoint"
 *    queryFields={["name", "description"]}
 *    filter={() => new ApiFilter().where("status", "ACTIVE")}
 *    sort={{ field: "name", direction: "ASC" }}
 *  />
 *
 *
 * @see DataLayerSearchSelect.propTypes for detailed description of the props.
 * @returns
 */
export default function DataLayerSearchSelect($props) {
  const {
    ident,
    mode,
    label,
    queryFields = [],
    numericQueryFields = [],
    prefilterSelection = true,
    filter = () => new ApiFilter(),
    sort,
    filterFunc,
    ...props
  } = $props;

  const data = useDataLayerQuery({ ident: ident, mode: mode });

  const fetchData = (page, q) => {
    const f = filter();
    if (q) {
      if (queryFields.length === 0 && numericQueryFields.length === 0) {
        // eslint-disable-next-line no-console
        console.warn(
          `DataLayerSearchSelect with ident ${ident} has no queryFields defined, but is trying to search with query "${q}". Please provide queryFields to enable searching.`
        );
        return;
      }

      const numericValue =
        numericQueryFields.length > 0 && /^\d+$/.test(q)
          ? parseInt(q, 10)
          : null;

      f.or((f) => {
        queryFields.forEach((field) => {
          f.where(field, "LIKE_IC", `%${q}%`);
        });
        if (numericValue !== null) {
          numericQueryFields.forEach((field) => {
            f.where(field, "EQ", numericValue);
            f.where(field, "EQ", -numericValue);
          });
        }
      });
    }

    if (!q && prefilterSelection) {
      // when there is no search query, we want to include the currently selected item in the options,
      // so that the select doesn't end up with an empty value and the user can see what is currently selected

      let selectedId = props.formField?.value;
      if (typeof selectedId === "object" && selectedId !== null) {
        if (typeof props.optionId === "function") {
          selectedId = props.optionId(selectedId);
        } else if (typeof props.optionId === "string") {
          selectedId = selectedId[props.optionId];
        } else {
          selectedId = selectedId.id;
        }
      }
      if (selectedId) {
        f.where(props.optionValue || "id", "EQ", selectedId);
      }
    }

    data.query({ page, filter: f, sort });
  };

  return (
    <>
      <SearchSelect
        label={label === undefined ? ident : label}
        {...props}
        getOptionsState={() =>
          filterFunc ? filterFunc(data.result?.content) : data.result?.content
        }
        getPageNumberState={() => 0}
        getLoadingState={() => data.isLoading}
        onFetchData={fetchData}
      />
    </>
  );
}

DataLayerSearchSelect.propTypes = {
  ...SearchSelect.propTypes,
  getPageNumberState: PropTypes.func,
  onFetchData: PropTypes.func,
  getLoadingState: PropTypes.func,
  getOptionsState: PropTypes.func,

  // ident is required to know which data layer resource to query
  ident: PropTypes.string.isRequired,

  // queryFields is an array of strings that represent the fields
  // to search in when the user types in the search input
  queryFields: PropTypes.arrayOf(PropTypes.string),

  // filter is a function that returns an ApiFilter instance,
  // it is used to build the search filter
  filter: PropTypes.func,

  // sort can be any value that the data layer supports for sorting,
  // it is passed directly to the query method of the data layer
  sort: PropTypes.any,

  // prefilterSelection is a boolean that determines whether to include the currently selected item in the options
  // when there is no search query. It defaults to true.
  prefilterSelection: PropTypes.bool,
};
